/**
 * PlotexEngineOptions — Wave 2 Task 3A.
 *
 * Configuration surface for the Plotex runtime. Wave 3 Task 3B will
 * consume this via `createPlotexEngine(options)`. For now it's the
 * canonical type definition — main.ts and individual modules can import
 * it to ensure consistency.
 *
 * Every field is optional. Defaults are documented inline and match the
 * current BB2 / arch_exterior behavior so a bare `createPlotexEngine({
 * canvas })` call produces an identical result to today's main.ts.
 */

import type { PostProcessPreset } from './core/PlotexPostProcessing';

export interface PlotexEngineOptions {
  /** The <canvas> element to render into. Required. */
  canvas: HTMLCanvasElement;

  /** URL of the scene manifest GLB. Default: `/chunks/scene.glb` */
  sceneUrl?: string;

  /** Path prefix for progressive chunk fetches. Default: `/chunks/` */
  chunksPrefix?: string;

  /**
   * Progress callback invoked while `scene.glb` streams in. Receives a
   * ratio in [0, 1] — use it to drive a loading bar in the host page.
   * Fires ~10-50 times during a cold load depending on network chunking;
   * safe to read `ratio * 100 | 0` for a percentage.
   *
   * Not called AFTER the load completes. Host code should hide the
   * loading overlay when the `createPlotexEngine()` promise resolves.
   */
  onProgress?: (ratio: number) => void;

  /** Paths to locally-hosted WASM decoders. */
  decoderPaths?: {
    /** Draco decoder directory. Default: `/draco/` */
    draco?: string;
    /** BasisU transcoder directory. Default: `/basis/` */
    basis?: string;
  };

  /** HDRI environment / IBL configuration. */
  environment?: {
    /** Path to a KTX2 or HDR file. Overrides `PLOTEX_environment` extension. */
    hdrPath?: string;
    /** Background color when no HDR is loaded. Default: `0x1a1c22` */
    fallbackColor?: number;
  };

  /** Shadow map configuration. */
  shadows?: {
    /** Enable shadow rendering. Default: `true` */
    enabled?: boolean;
    /** Shadow map resolution (px). Default: `2048` */
    mapSize?: number;
    /** Shadow bias to avoid acne. Default: `-0.0005` */
    bias?: number;
  };

  /** Post-processing configuration. */
  postprocess?: {
    /** Quality preset. Default: `'medium'` */
    preset?: PostProcessPreset;
  };

  /** LOD manager tuning. */
  lod?: {
    /** Triangles-per-pixel coverage target. Default: `0.5` */
    trisPerPixelTarget?: number;
    /** Meshes below this screen radius (px) skip LOD upgrades. Default: `2` */
    minScreenRadiusPx?: number;
    /** Minimum ms between LOD update ticks. Default: `150` */
    updateIntervalMs?: number;
    /** Max concurrent in-flight chunk fetches. Default: `6` */
    maxInflight?: number;
    /** Worker pool size. Default: `min(4, hardwareConcurrency - 2)` */
    poolSize?: number;
    /** FPS target for adaptive downgrade. Default: `45` */
    fpsTarget?: number;
    /** Eagerly upgrade LODs as camera approaches. Default: `true` */
    upgradeEagerly?: boolean;
    /** Downgrade LODs when FPS drops below target. Default: `true` */
    downgradeUnderLoad?: boolean;
  };

  /** Exponential backoff for failed chunk fetches. */
  backoff?: {
    /** Initial backoff duration (ms). Default: `5000` */
    initialMs?: number;
    /** Maximum backoff cap (ms). Default: `60000` */
    maxMs?: number;
  };

  /**
   * Impostor runtime swap — OPT-IN, see [plotex-hook:impostor] in src/index.ts.
   *
   * When enabled AND a mesh authored in Blender has
   * `PLOTEX_authoring.is_impostor_source`, the engine replaces that
   * mesh with a camera-facing quad using `PlotexImpostorMaterial`
   * once the camera is farther than `switchDistance`, cross-fading
   * across a narrow band to avoid pop-in.
   *
   * DEFAULT: `enabled: false`. Current atlas quality doesn't reach
   * "invisible swap" parity with real foliage at typical arch-viz
   * distances — baked quads are visibly lower-fidelity than the
   * source mesh. Consumers who know they need the FPS savings on
   * huge scenes opt in explicitly. Once the atlas-bake quality
   * improves (shadow-aware tile lighting + alpha-matte refinement)
   * the default can flip.
   */
  impostor?: {
    /** Distance (meters) at which real mesh swaps to impostor quad. Default: 50 */
    switchDistance?: number;
    /** Blend band width (meters) around switchDistance for smooth crossfade. Default: 0.5 */
    crossfadeBand?: number;
    /** Enable/disable the impostor system. Default: `false`. */
    enabled?: boolean;
  };

  /**
   * Click-to-measure tool — see src/ui/PlotexMeasureTool.ts. When enabled,
   * the engine builds the tool and exposes a toolbar button next to the
   * quality slider; clicking the button toggles measuring mode. Tool is
   * constructed but inactive by default; user opts in via the button (or
   * programmatically via `engine.measureTool.activate()`).
   */
  measureTool?: {
    /** Default: true — tool is built, off by default. */
    enabled?: boolean;
  };

  /**
   * Section plane tool — click to cut through the scene with a
   * draggable clipping plane. Users switch axis (X/Y/Z), slide the
   * offset, and flip the clipped side via an on-screen panel.
   */
  sectionTool?: {
    /** Build the tool and show its toolbar button. Default: true. */
    enabled?: boolean;
  };

  /**
   * WebXR / VR integration. When enabled, the engine toggles
   * `renderer.xr.enabled = true`, drives its animate loop through
   * `renderer.setAnimationLoop` (the only way to render inside the XR
   * frame callback), and appends a three.js `VRButton` to the toolbar
   * stack. On browsers without WebXR support the button renders as
   * "VR NOT SUPPORTED" — we leave it visible on purpose so users can
   * see the capability gap rather than wonder whether the feature is
   * silently missing.
   */
  xr?: {
    /** Show the VR button and enable `renderer.xr`. Default: `true` */
    enabled?: boolean;
  };

  /** Camera setup. */
  camera?: {
    /** Vertical field of view (degrees). Default: `60` */
    fov?: number;
    /** Near clipping plane. Default: `0.1` */
    near?: number;
    /** Far clipping plane. Default: `5000` */
    far?: number;
    /** Initial camera position `[x, y, z]`. Default: `[200, 150, 200]` */
    initialPosition?: [number, number, number];
    /**
     * Default camera mode. `'orbit'` uses OrbitControls (drag to rotate,
     * scroll to zoom). `'walk'` boots directly into PointerLockControls +
     * WASD FPS navigation. `'none'` disables all controls (useful for
     * scripted camera paths or turntable renders). Default: `'orbit'`.
     *
     * The Walk/Orbit toolbar button is available regardless of this
     * setting — it only determines which mode is active at startup.
     */
    controls?: 'orbit' | 'walk' | 'none';
    /**
     * Walk-mode base movement speed in meters/second. Default: `5`
     * (roughly a brisk pedestrian pace; raise for scale-exaggerated
     * scenes where 1 unit != 1 meter).
     */
    walkSpeed?: number;
    /**
     * Walk-mode sprint speed (held with Shift) in meters/second.
     * Default: `12` (~2.4× base, the platformer walk/run ratio).
     */
    walkSprintSpeed?: number;
  };
}

/**
 * Deep-merge utility for PlotexEngineOptions. Merges `overrides` into
 * a copy of `defaults`, returning a fully-populated config. Only handles
 * the two-level nesting depth that PlotexEngineOptions uses (no recursive
 * deep-merge needed).
 */
export function resolveOptions(
  overrides: PlotexEngineOptions,
): Required<
  Pick<PlotexEngineOptions, 'canvas' | 'sceneUrl' | 'chunksPrefix'>
> &
  PlotexEngineOptions {
  return {
    canvas: overrides.canvas,
    sceneUrl: overrides.sceneUrl ?? '/chunks/scene.glb',
    chunksPrefix: overrides.chunksPrefix ?? '/chunks/',
    decoderPaths: {
      draco: '/draco/',
      basis: '/basis/',
      ...overrides.decoderPaths,
    },
    environment: {
      fallbackColor: 0x1a1c22,
      ...overrides.environment,
    },
    shadows: {
      enabled: true,
      mapSize: 2048,
      bias: -0.0005,
      ...overrides.shadows,
    },
    postprocess: {
      preset: 'medium',
      ...overrides.postprocess,
    },
    lod: {
      trisPerPixelTarget: 0.5,
      minScreenRadiusPx: 2,
      updateIntervalMs: 150,
      maxInflight: 6,
      poolSize: Math.min(4, Math.max(1, (navigator.hardwareConcurrency ?? 4) - 2)),
      fpsTarget: 45,
      upgradeEagerly: true,
      downgradeUnderLoad: true,
      ...overrides.lod,
    },
    backoff: {
      initialMs: 5000,
      maxMs: 60000,
      ...overrides.backoff,
    },
    impostor: {
      switchDistance: 50,
      crossfadeBand: 0.5,
      // OFF by default — the baked atlases trade visibly for FPS on huge
      // scenes, but at typical arch-viz viewing distances they're clearly
      // lower-fidelity than the source mesh and the swap reads as "ugly
      // flat palm" instead of "optimized palm". Turn on explicitly:
      //     createPlotexEngine({ ..., impostor: { enabled: true } })
      // if you need the FPS win on a large masterplan.
      enabled: false,
      ...overrides.impostor,
    },
    measureTool: {
      enabled: true,
      ...overrides.measureTool,
    },
    sectionTool: {
      enabled: true,
      ...overrides.sectionTool,
    },
    xr: {
      enabled: true,
      ...overrides.xr,
    },
    camera: {
      fov: 60,
      near: 0.1,
      far: 5000,
      initialPosition: [200, 150, 200],
      controls: 'orbit',
      walkSpeed: 5,
      walkSprintSpeed: 12,
      ...overrides.camera,
    },
  };
}
